import { apiFetcher } from "@/services/API.service";
import { UserNotification, UserNotificationSchema } from "@/types";
import { validateArrayWithSchema } from "@/utils/dataValidators.zod";

// #region fetchNotifications
export async function fetchNotifications({
  showUnread,
  signal,
  retry = false,
}: {
  showUnread: boolean;
  signal?: AbortSignal;
  retry?: boolean;
}): Promise<
  | {
      rStatus: "success";
      notifications: UserNotification[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchNotifications");
    }
    const response = await apiFetcher({
      route: "/notifications/get",
      method: "POST",
      signal: signal,
      options: {
        body: {
          status: showUnread ? "Incomplete" : "Complete",
        },
      },
    });

    if (response.status === 200) {
      console.log(response.data);

      const validatedData = validateArrayWithSchema<UserNotification>(
        response.data,
        UserNotificationSchema,
        "UserNotification"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message:
            "invalid response: data is not an array of valid notifications",
        };
      }

      return {
        rStatus: "success",
        notifications: validatedData,
      };
    }

    if (response.status === 204) {
      return {
        rStatus: "success",
        notifications: [],
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return await fetchNotifications({
          showUnread,
          signal: signal,
          retry: true,
        });
      }

      return { rStatus: "error", message: "network_error" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion fetchNotifications

// #region sendNotificationRead
export async function sendNotificationRead({
  notificationID,
  signal,
}: {
  notificationID: number;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/notifications/markRead",
      method: "POST",
      signal: signal,
      options: {
        body: {
          notificationId: notificationID,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion sendNotificationRead
